import 'src/screen/Protected/Wizards/Wizards.scss';
import variables from 'src/style/variable/variables.module.scss';
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Map, List } from 'immutable';
import { Helmet } from 'react-helmet';
import classNames from 'classnames';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import CircleOutlinedIcon from '@mui/icons-material/RadioButtonUncheckedOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ErrorIcon from '@mui/icons-material/Error';
import DeviceSelectorField from 'src/component/UI/DeviceSelectorField';
import Button from 'src/component/UI/Button';
import { clearDeviceSettingsSnapshot, upgradeDevice, fetchCommLoops, validateUpgradeDevice } from 'src/module/device/action';
import { getApplySnapshotStatus, getCommLoops, getUpgradeViolations, getUpgradeMisconfigurationErrors } from 'src/module/device/selector';
import LoadingSpinner from 'src/component/UI/LoadingSpinner';
import CircularWithValueLabel from 'src/component/UI/CircularProgressWithLabel';
import { isWhitelabel } from 'src/utils/utils';
import { getPossibleUpgradePaths } from 'src/module/upgrade/selector';
import { getLabelForDeviceModel } from 'src/i18n';
import { clearDeviceUpgradeLoading, fetchDeviceUpgrades } from 'src/module/upgrade/action';
import { FormControl, InputLabel, MenuItem, Select, TextField, Tooltip } from '@mui/material';
import DeviceLink from 'src/component/UI/DeviceLink';
import { useTranslation } from 'src/i18n';
import UpgradeViolationsModal from './UpgradeViolationsModal';

export default function UpgradeDeviceScreen () {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const reprogramStatus: any = useSelector(getApplySnapshotStatus);
  const upgradeViolations: any = useSelector(getUpgradeViolations);
  const upgradeMisconfigurationErrors: any = useSelector(getUpgradeMisconfigurationErrors);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedDeviceFactory, setSelectedDeviceFactory] = useState('');
  const [replacementDeviceFactory, setReplacementDeviceFactory] = useState('');
  const [deviceCommLoop, setDeviceCommLoop] = useState(null);
  const [reprogramStarted, setReprogramStarted] = useState(false);
  const [isChangeCommsDataChecked, setIsChangeCommsDataChecked] = useState(false);
  const [violationsModalOpen, setViolationsModalOpen] = useState(false);
  const comms = useSelector(getCommLoops);
  const commLoops = [{ label: 'None', value: 'null' }];
  comms?.forEach((loop: any) => {
    const id = loop?.get('id');
    const label = loop?.get('tag');
    commLoops.push({ label, value: id });
  });

  let potentialUpgradeDevices: any = useSelector(getPossibleUpgradePaths(selectedDevice?.device_factory));
  if (selectedDevice && selectedDeviceFactory && !potentialUpgradeDevices.find((deviceFactory => deviceFactory === selectedDeviceFactory))) {
    potentialUpgradeDevices = potentialUpgradeDevices.concat(selectedDeviceFactory);
  }
  const [deviceAddress, setDeviceAddress] = useState('');

  useEffect(() => {
    if (selectedDevice) {
      setSelectedDeviceFactory(selectedDevice?.device_factory || null);
    }
    else {
      setSelectedDeviceFactory('');
    }
  }, [selectedDevice]);

  useEffect(() => {
    dispatch(fetchDeviceUpgrades());
    dispatch(clearDeviceUpgradeLoading());
    dispatch(fetchCommLoops());
  }, []);

  const [sectionCollapsed, setSectionCollapsed] = useState(Map({
    step1: false,
    step2: true,
    step3: true
  }));

  const [completedSteps, setCompletedSteps] = useState(Map({
    step1: false,
    step2: false,
    step3: false
  }));

  useEffect(() => {
    dispatch(clearDeviceSettingsSnapshot());
  }, []);

  const makeToggleSection = (section) => {
    return () => {
      toggleSectionCollapsed(section);
    };
  };

  const handleChange = (value) => {
    if ((value === null || value === '' || value.id === '')) {
      setSelectedDevice(null);
    } else {
      setSelectedDevice(value);
      handleStep1Submit();
    }
  };

  const toggleSectionCollapsed = (section: string) => {
    setSectionCollapsed(sectionCollapsed.set(section, !sectionCollapsed.get(section)));
  };

  const toggleCompletedStep = (step: string) => {
    setCompletedSteps(completedSteps.set(step, !completedSteps.get(step)));
  };

  const handleStep1Submit = () => {
    toggleCompletedStep('step1');
    setSectionCollapsed(Map({ step1: true, step2: false, step3: true }));
  };

  const handleStep2Submit = () => {
    toggleCompletedStep('step2');
    setSectionCollapsed(Map({ step1: true, step2: true, step3: false }));
  };

  const handleValidateAndReprogram = () => {
    dispatch(validateUpgradeDevice(selectedDevice?.id, replacementDeviceFactory));
    setViolationsModalOpen(true);
  };

  const handleConfirmReprogram = () => {
    setViolationsModalOpen(false);
    setReprogramStarted(true);
    dispatch(upgradeDevice(selectedDevice?.id, replacementDeviceFactory, deviceCommLoop, deviceAddress));
  };

  const handleCancelViolationsModal = () => {
    setViolationsModalOpen(false);
    dispatch(clearDeviceSettingsSnapshot());
  };

  const getReprogramProgress = (): number => {
    if (devicesToReprogram?.every(device => device.get('done'))) {
      return 1;
    }
    else if (reprogramStarted && !reprogramStatus.get('progress')) {
      return 0.1;
    }
    return reprogramStatus.get('progress');
  };


  const devicesToReprogram: List<any> = reprogramStatus.get('snapshotDevices', List());

  const numDevicesToReprogram: number = reprogramStatus.get('numDevices', 0);
  const reprogramProgress: number = getReprogramProgress();
  const title = isWhitelabel() ? 'ATCOM' : 'SmartTrace';

  const handlereplacementDeviceFactory = (factory) => {
    setReplacementDeviceFactory(factory);
  };

  const handleCommLoopsCheckboxChange = () => {
    if (isChangeCommsDataChecked) {
      setDeviceCommLoop(null);
      setDeviceAddress('');
    }
    setIsChangeCommsDataChecked(!isChangeCommsDataChecked);
  };

  const handleCommLoopChange = (event) => {
    setDeviceCommLoop(event?.target?.value ?? null);
  };

  const overAllStatus = () => {
    if (sectionCollapsed.get('step1')) {
      return <CheckCircleOutlinedIcon className='complete'/>;
    }
    return <CircleOutlinedIcon />;
  };

  const deviceStatus = (device) => {
    if (device.get('done')) {
      return <div className='device-status-loaded complete'>
        <CheckCircleOutlinedIcon width={20} />
      </div>;
    }
    if (reprogramStatus.get('status') === 'failed') {
      return <Tooltip title={t('upgradeDevice.unableToCommunicate')}>
        <div className='device-status-loaded complete-with-error '><ErrorIcon width={20} /> </div>
      </Tooltip>;
    }
    return  <div className='device-status-loaded'>
      <LoadingSpinner color={variables.greyDark } />
    </div>;
  };

  const step3Status = () => {
    if (reprogramStarted) {
      if (reprogramStatus.get('status') === 'failed') {
        return <ErrorIcon className='complete-with-error' width={28} />;
      }
      return <CircularWithValueLabel size={28} interval={50} hideLabel value={reprogramProgress * 100} />;
    }
    return <CircleOutlinedIcon />;
  };

  return (
    <div id='upgrade-device' className='guide-wizard page'>
      <Helmet>
        <title>{title} - {t('wizards.upgradeReplaceDevice')}</title>
      </Helmet>

      <Grid item xs={12}>
        <h2>{t('wizards.upgradeReplaceDevice')}</h2>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper>
            <h2 className={classNames('expandable', sectionCollapsed.get('step1') ? 'collapsed' : 'expanded')} onClick={makeToggleSection('step1')}>
              <div>
                {overAllStatus()}
                <span>{t('upgradeDevice.step1Title')}</span>
              </div>
              <span className='expand-arrow'>{sectionCollapsed.get('step1') ? '▲' : '▼'}</span>
            </h2>
            <div className={classNames('inner-section', sectionCollapsed.get('step1') && 'collapsed')} >
              <p>{t('upgradeDevice.step1Description')}</p>
              <div className='select-device-bar'>
                <DeviceSelectorField onChange={handleChange} hideLabel value={selectedDevice} type='controller' />
              </div>
              <div className='button-bar'>
                <Button disabled={!selectedDevice} onClick={handleStep1Submit} cta>{t('common.next')}</Button>
              </div>
            </div>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper>
            <h2 className={classNames('expandable', sectionCollapsed.get('step2') ? 'collapsed' : 'expanded')} onClick={makeToggleSection('step2')}>
              <div>
                {completedSteps.get('step2') ? (<CheckCircleOutlinedIcon className='complete' />) : (<CircleOutlinedIcon />)}
                <span>{t('upgradeDevice.step2Title')}</span>
              </div>
              <span className='expand-arrow'>{sectionCollapsed.get('step2') ? '▲' : '▼'}</span>
            </h2>
            {!sectionCollapsed.get('step2') ? <div className='inner-section'>
              <p>{t('upgradeDevice.step2Description')}</p>
              <h3>{t('upgradeDevice.selectReplacement')}</h3>
              <div className="squares-container">
                {potentialUpgradeDevices.toJS().map((factory, index) => (
                  <div key={`potential-upgrade-${index}`} className="square" onClick={() => handlereplacementDeviceFactory(factory)}>
                    <Paper className={classNames('squares-paper', (replacementDeviceFactory === factory) && 'selected-device')}>
                      <div style={{ fontSize: '1.2em', marginBottom: '5px' }}>{getLabelForDeviceModel(factory.replace('Controller', ''))}</div>
                      {factory.replace('Controller', '') === selectedDeviceFactory.replace('Controller', '') && (
                        <div style={{ fontSize: '0.8em', textAlign: 'center' }}>{t('upgradeDevice.sameModel')}</div>
                      )}
                    </Paper>
                  </div>
                ))}
              </div>
              <div className='button-bar'>
                <Button onClick={handleStep2Submit} disabled={!replacementDeviceFactory} cta>{t('common.next')}</Button>
              </div>
            </div> : null}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper>
            <h2 className={classNames('expandable')} onClick={makeToggleSection('step3')}>
              <div>
                {step3Status()}
                <span>{t('upgradeDevice.step3Title')}</span>
              </div>
              <span className='expand-arrow'>{sectionCollapsed.get('step3') ? '▲' : '▼'}</span>
            </h2>
            {!sectionCollapsed.get('step3') ? <div className='inner-section'>
              {numDevicesToReprogram > 1 ? (<>
                <h4>{t('upgradeDevice.reprogramming')}</h4>
                <div className='device-status-container'>
                  {devicesToReprogram?.valueSeq().map((device) => {
                    return (
                      <div className='device-status' key={device.get('deviceId')}>
                        {deviceStatus(device)}

                        <div className='device-status-tag'>
                          <DeviceLink label={device.get('tag')} circuitTag={(device.get('tag') !== selectedDevice.tag) && device.get('tag')} controllerTag={selectedDevice?.tag}/>
                        </div>

                      </div>
                    );
                  })}
                </div>
              </>) : <>
                {
                  <div className='commloop-container'>
                    <label>
                      <input
                        type="checkbox"
                        checked={isChangeCommsDataChecked}
                        onChange={handleCommLoopsCheckboxChange}
                      />
                      {t('upgradeDevice.changeCommsData')}
                    </label>
                    {isChangeCommsDataChecked && (
                      <FormControl fullWidth>
                        <InputLabel id="comms-select-label">{t('upgradeDevice.commLoop')}</InputLabel>
                        <Select
                          labelId="comms-select-label"
                          value={deviceCommLoop ?? ''}
                          onChange={handleCommLoopChange}
                        >
                          {commLoops.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                        <TextField
                          fullWidth
                          id="deviceAddress"
                          name="deviceAddress"
                          label={t('upgradeDevice.address')}
                          type="number"
                          value={deviceAddress}
                          onChange={(event) => { setDeviceAddress(event?.target?.value); }}
                          variant='outlined'
                          InputLabelProps={{ shrink: false }}
                        />
                      </FormControl>
                    )}
                  </div>

                }
              </>}
              <UpgradeViolationsModal
                open={violationsModalOpen && upgradeViolations !== null}
                violations={upgradeViolations ? upgradeViolations.toJS() : []}
                misconfigurationErrors={upgradeMisconfigurationErrors ? upgradeMisconfigurationErrors.toJS() : []}
                onConfirm={handleConfirmReprogram}
                onCancel={handleCancelViolationsModal}
              />
              <div className='button-bar'>
                <Button disabled={!selectedDevice} onClick={handleValidateAndReprogram} cta>{t('upgradeDevice.reprogramNewDevice')}</Button>
              </div>
            </div> : null}
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
}
